/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useState } from 'react';

import { css, jsx } from '@compiled/react';
import endOfQuarter from 'date-fns/endOfQuarter';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import enUS from 'date-fns/locale/en-US';
import startOfQuarter from 'date-fns/startOfQuarter';

import Button, { IconButton } from '@atlaskit/button/new';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { formatIsoLocalDate, formatWithLocale } from '@atlassian/townsquare-date-utils/date-format';

import { getInitialMonthPickerDate } from '../../../common/utils/utils';
import type { CalendarProps, CalendarState } from '../../../types';

const yearPickerContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
});

const quarterPickerContainerStyles = css({
	marginTop: token('space.200', '16px'),
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'space-around',
	height: '225px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'> button': {
		flex: '0 0 50%',
		height: '105px',
		'&:hover, &:disabled': {
			height: '105px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
		'> span': {
			alignSelf: 'center',
		},
	},
});

const quarterPickerWrapperStyles = xcss({
	padding: 'space.200',
});

const getQuarterData = (locale: Locale) => {
	const today = new Date();
	const quarters = [];
	for (let i = 0; i < 12; i += 3) {
		quarters.push({
			months: [i, i + 1, i + 2],
			name: `${formatWithLocale(
				new Date(today.getFullYear(), i),
				'MMM',
				locale,
			)}-${formatWithLocale(new Date(today.getFullYear(), i + 2), 'MMM', locale)}`,
		});
	}
	return quarters;
};

export const QuarterPicker = (props: CalendarProps): JSX.Element => {
	const [date, setDate] = useState(getInitialMonthPickerDate(props));

	const { selectedDate, dateType, onSelect, minDate, maxDate } = props;
	const { month, year, initialYear } = date;

	const locale = enUS;

	const onDateChange = useCallback(
		(newDate: CalendarState) => {
			setDate(newDate);
			const newDateObject = new Date(newDate.year, newDate.month);
			const newDateInterval = {
				start: formatIsoLocalDate(startOfQuarter(newDateObject), locale),
				end: formatIsoLocalDate(endOfQuarter(newDateObject), locale),
			};
			onSelect(newDateInterval);
		},
		[locale, onSelect],
	);
	const QUARTER_DATA = getQuarterData(locale);

	const isQuarterDisabled = useCallback(
		(isSelected: boolean, endQuarter: number) => {
			const currentQuarter = new Date(year, endQuarter);
			const quarterStart = startOfQuarter(currentQuarter);
			const quarterEnd = endOfQuarter(currentQuarter);
			if (minDate || maxDate) {
				if (minDate && quarterEnd < minDate) {
					return true;
				}
				if (maxDate && quarterStart > maxDate) {
					return true;
				}
			}
			if (!selectedDate) {
				return false;
			}
			const currentDate = new Date(year, endQuarter);
			return (
				!isSelected &&
				(dateType === 'start'
					? isBefore(selectedDate, currentDate)
					: isAfter(selectedDate, currentDate))
			);
		},
		[dateType, selectedDate, year, minDate, maxDate],
	);

	return (
		<Box xcss={quarterPickerWrapperStyles} testId="quarter-picker-wrapper">
			<Box xcss={yearPickerContainerStyles} testId="year-picker-container">
				<IconButton
					appearance="subtle"
					spacing="compact"
					onClick={() => setDate({ initialYear, month, year: year - 1 })}
					label="Button to see previous year"
					icon={(iconProps) => <ChevronLeftIcon {...iconProps} size="small" />}
				/>
				<b>{year}</b>
				<IconButton
					appearance="subtle"
					spacing="compact"
					onClick={() => setDate({ initialYear, month, year: year + 1 })}
					label="Button to see next year"
					icon={(iconProps) => <ChevronRightIcon {...iconProps} size="small" />}
				/>
			</Box>
			<div
				css={quarterPickerContainerStyles}
				data-testid="quarter-picker-container"
				role="radiogroup"
			>
				{QUARTER_DATA.map((item) => {
					const startQuarter = item.months[0];
					const endQuarter = item.months[2];
					const isSelected = date.initialYear === year && item.months.includes(month);
					return (
						<Button
							key={startQuarter}
							appearance="subtle"
							onClick={() => onDateChange({ initialYear, year, month: endQuarter })}
							isSelected={isSelected}
							aria-checked={isSelected}
							aria-label={item.name}
							role="radio"
							isDisabled={isQuarterDisabled(isSelected, endQuarter)}
						>
							{item.name}
						</Button>
					);
				})}
			</div>
		</Box>
	);
};
